/**
 * Pure RUI-lookup view helpers for the public wizard
 * (PR-ONBOARDING-RUI-AUTO-LOOKUP-UX-MATCH-1 + STALE-FIX).
 *
 * No runtime `@/` imports (the `@/lib/api` import is type-only → elided by
 * esbuild/tsx) so these can be unit-tested from the repo root. `useRuiLookup`
 * re-exports everything here.
 *
 * STALE-FIX: a lookup result is valid ONLY for the exact tuple of inputs it was
 * computed from. `ruiLookupKey` derives that tuple; `isRuiLookupStale` compares
 * it with the current form key. A stale `VERIFIED` is never shown — the badge
 * becomes "RUI da riverificare." until a fresh lookup runs.
 */
import type { RuiLookupApiInput, RuiLookupApiResult } from '@/lib/api';

export type RuiLookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; result: RuiLookupApiResult; key: string }
  | { status: 'unavailable' };

/** Subset of fields a lookup result depends on (its validity key). */
export interface RuiLookupKeyInput {
  subject: 'ORGANIZATION' | 'RESPONSIBLE';
  orgType?: string | null;
  legalNature?: string | null;
  ruiNumber?: string | null;
  declaredName?: string | null;
  organizationRuiNumber?: string | null;
}

/**
 * Validity key for a lookup result. Structural fields are trimmed; the RUI
 * number is uppercased+trimmed (matching BE canonicalization intent); the
 * declared name is uppercased + whitespace-collapsed so cosmetic edits don't
 * churn but a genuinely different name invalidates. `organizationRuiNumber`
 * only participates for RESPONSIBLE (it drives the link check).
 */
export function ruiLookupKey(input: RuiLookupKeyInput): string {
  const t = (v: string | null | undefined): string => (v ?? '').trim();
  const u = (v: string | null | undefined): string => t(v).toUpperCase();
  const n = (v: string | null | undefined): string =>
    t(v).toUpperCase().replace(/\s+/g, ' ');
  const orgRui =
    input.subject === 'RESPONSIBLE' ? u(input.organizationRuiNumber) : '';
  // Unit separator (U+241F) can't appear in user input → unambiguous join.
  return [
    input.subject,
    t(input.orgType),
    t(input.legalNature),
    u(input.ruiNumber),
    n(input.declaredName),
    orgRui,
  ].join('␟');
}

/** True when the stored result was computed for a different input tuple. */
export function isRuiLookupStale(
  state: RuiLookupState,
  currentKey: string,
): boolean {
  return state.status === 'done' && state.key !== currentKey;
}

/** Format an ISO date (YYYY-MM-DD) as Italian GG/MM/AAAA. */
export function formatDateIT(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Banner tone for a lookup result (UI helper). A `stale` result (inputs changed
 * since it was computed) is HIDDEN — `null` — so no badge shows while the user
 * is still editing (UX-INPUT-1; the lookup re-runs on blur). `unavailable` →
 * warning.
 */
export function ruiLookupTone(
  state: RuiLookupState,
  stale = false,
): 'info' | 'warning' | 'danger' | null {
  if (state.status === 'idle' || state.status === 'loading') return null;
  if (stale) return null;
  if (state.status === 'unavailable') return 'warning';
  const r = state.result;
  const blocking =
    !r.found ||
    !r.sectionOk ||
    !r.subjectKindOk ||
    r.outcome === 'NAME_MISMATCH' ||
    r.outcome === 'INOPERATIVE' ||
    r.linkStatus === 'USER_NOT_LINKED_TO_ORG' ||
    r.linkStatus === 'LINKED_TO_OTHER_INTERMEDIARY';
  if (blocking) return 'danger';
  if (r.outcome === 'REVIEW_REQUIRED' || r.nameMatch === 'REVIEW_REQUIRED')
    return 'warning';
  return 'info';
}

/**
 * Display message for a lookup result — composed FE-side so the copy stays
 * clean. A `stale` result (inputs changed since it was computed) is HIDDEN —
 * `null` — so the old outcome never lingers while editing (UX-INPUT-1). The
 * success badge confirms the NAME match (no section/date repeated — the date
 * stays in the read-only field). The BE `message` is the fallback / log text.
 */
export function ruiLookupMessage(
  state: RuiLookupState,
  stale = false,
): string | null {
  if (state.status === 'idle' || state.status === 'loading') return null;
  if (stale) return null;
  if (state.status === 'unavailable')
    return 'Verifica RUI non disponibile ora — il dato sarà verificato all’invio.';
  const r = state.result;
  const isPerson = r.subjectKind === 'PERSON';

  if (!r.found) return 'Numero di iscrizione al RUI non trovato.';
  if (
    r.linkStatus === 'USER_NOT_LINKED_TO_ORG' ||
    r.linkStatus === 'LINKED_TO_OTHER_INTERMEDIARY'
  )
    return 'Il responsabile indicato non risulta collegato alla società nel Registro IVASS.';
  if (!r.sectionOk || r.outcome === 'SECTION_MISMATCH')
    return 'Il numero RUI indicato non appartiene alla sezione attesa.';
  if (r.outcome === 'INOPERATIVE')
    return 'Il soggetto risulta inoperativo nel Registro IVASS.';
  if (r.outcome === 'NAME_MISMATCH')
    return 'La denominazione non corrisponde a quella iscritta al RUI.';
  if (r.outcome === 'REVIEW_REQUIRED' || r.nameMatch === 'REVIEW_REQUIRED')
    return isPerson
      ? 'RUI responsabile trovato. Nominativo parzialmente coerente: sarà validato da un operatore.'
      : 'RUI trovato. Denominazione parzialmente coerente: sarà validata da un operatore.';
  // VERIFIED — confirm the name match (denominazione for COMPANY, nominativo
  // for a PERSON: org-as-person or responsible). Date is NOT repeated here.
  if (r.nameMatch === 'VERIFIED')
    return isPerson
      ? 'Il nominativo corrisponde a quello iscritto al RUI.'
      : 'La denominazione corrisponde a quella iscritta al RUI.';
  return 'RUI verificato.';
}

/** Re-export for call-site convenience (key derivation accepts the api input). */
export type { RuiLookupApiInput };
